import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/index';

export interface RequestOptions {
  headers?: HttpHeaders | Record<string, string>;
  params?: HttpParams | Record<string, string>;
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  get<T>(endpoint: string, options?: RequestOptions): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}/${endpoint}`, this.createRequestOptions(options)).pipe(catchError(this.handleError));
  }

  post<T, D = any>(endpoint: string, body: D, options?: RequestOptions): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}/${endpoint}`, body, this.createRequestOptions(options)).pipe(catchError(this.handleError));
  }

  put<T, D = any>(endpoint: string, body: D, options?: RequestOptions): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}/${endpoint}`, body, this.createRequestOptions(options)).pipe(catchError(this.handleError));
  }

  delete<T>(endpoint: string, options?: RequestOptions): Observable<T> {
    return this.http.delete<T>(`${this.apiUrl}/${endpoint}`, this.createRequestOptions(options)).pipe(catchError(this.handleError));
  }

  patch<T, D = any>(endpoint: string, body: D, options?: RequestOptions): Observable<T> {
    return this.http.patch<T>(`${this.apiUrl}/${endpoint}`, body, this.createRequestOptions(options)).pipe(catchError(this.handleError));
  }

  private createRequestOptions(options?: RequestOptions): Record<string, any> {
    const requestOptions: Record<string, any> = {};

    if (!options) {
      return requestOptions;
    }

    if (options.headers) {
      requestOptions['headers'] = options.headers instanceof HttpHeaders ? options.headers : new HttpHeaders(options.headers);
    }

    if (options.params) {
      let httpParams = new HttpParams();
      if (options.params instanceof HttpParams) {
        httpParams = options.params;
      } else {
        Object.entries(options.params).forEach(([key, value]) => {
          httpParams = httpParams.set(key, value);
        });
      }
      requestOptions['params'] = httpParams;
    }

    if (options.responseType) {
      requestOptions['responseType'] = options.responseType;
    }

    if (options.withCredentials !== undefined) {
      requestOptions['withCredentials'] = options.withCredentials;
    }

    return requestOptions;
  }

  private handleError(error: any) {
    console.error('API Error:', error);
    return throwError(() => error);
  }
}
